import { Assets } from "pixi.js";
import { bundleKey, diffContent, pixiAlias, readContent, toPixiAssets, type ContentAsset } from "./content";

interface Registration {
  /** Pixi bundle id, unique per version of the bundle. */
  id: string;
  key: string;
  assets: ContentAsset[];
  /** Loaded assets by content alias. */
  loaded?: Record<string, unknown>;
}

/**
 * The release's content bundles as Pixi Assets bundles. Changed bundles get a new Pixi bundle, so
 * the old textures stay valid until the game has swapped them and calls release().
 */
export class RemoteContent {
  private readonly bundles = new Map<string, Registration>();
  private readonly replaced: Registration[] = [];
  private generation = 0;

  /** Registers new and changed bundles (compared by sha256). Returns the names that changed or were removed. */
  update(content: unknown): string[] {
    const next = readContent(content);
    const keys = new Map([...this.bundles].map(([name, bundle]) => [name, bundle.key]));
    const { changed, removed } = diffContent(keys, next);
    for (const name of [...changed, ...removed]) {
      const old = this.bundles.get(name);
      if (old) this.replaced.push(old);
      this.bundles.delete(name);
    }
    for (const name of changed) {
      const assets = next[name] ?? [];
      const id = `content:${name}:${++this.generation}`;
      Assets.addBundle(id, toPixiAssets(id, assets));
      this.bundles.set(name, { id, key: bundleKey(assets), assets });
    }
    return [...changed, ...removed];
  }

  /** Whether `name` is registered but not loaded yet, e.g. after a failed download. */
  needsLoad(name: string): boolean {
    const bundle = this.bundles.get(name);
    return bundle !== undefined && bundle.loaded === undefined;
  }

  /**
   * Loads a bundle, reporting progress from 0 to 1. Resolves its assets by alias, or undefined
   * when the bundle doesn't exist, fails to load or takes longer than `timeoutMs`. Never rejects.
   */
  async load(name: string, onProgress?: (progress: number) => void, timeoutMs?: number): Promise<Record<string, unknown> | undefined> {
    const bundle = this.bundles.get(name);
    if (!bundle) return undefined;
    if (bundle.loaded) return bundle.loaded;
    try {
      const result = (await withTimeout(Assets.loadBundle(bundle.id, onProgress), timeoutMs)) as Record<string, unknown>;
      const loaded: Record<string, unknown> = {};
      for (const asset of bundle.assets) {
        const value = result[pixiAlias(bundle.id, asset.alias)];
        if (value === undefined || value === null) throw new Error(`no parser could load ${asset.url}`);
        loaded[asset.alias] = value;
      }
      if (this.bundles.get(name) === bundle) bundle.loaded = loaded;
      return loaded;
    } catch (err) {
      console.warn(`content: bundle ${name} failed to load`, err);
      return undefined;
    }
  }

  /** Unloads replaced bundles. Call once nothing shows their textures any more. */
  async release(): Promise<void> {
    const old = this.replaced.splice(0);
    const inUse = new Set([...this.bundles.values()].flatMap((bundle) => bundle.assets.map((asset) => asset.url)));
    // Files are immutable, so an unchanged file of a changed bundle is the same texture: keep it.
    const keys = old.flatMap((bundle) =>
      bundle.assets
        .filter((asset) => !inUse.has(asset.url))
        .map((asset) => pixiAlias(bundle.id, asset.alias))
        .filter((key) => Assets.cache.has(key)),
    );
    if (keys.length === 0) return;
    await Assets.unload(keys).catch((err: unknown) => console.warn("content: unloading failed", err));
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number | undefined): Promise<T> {
  if (timeoutMs === undefined) return promise;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timed out after ${timeoutMs} ms`)), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
